// [PACOTE D - AGENDA E DURACAO]
// Helper puro compartilhado para normalização e cálculo canônico de duração e conflitos de agendamento.
// Canônico: duracao_segundos (INTEGER POSITIVO)
// Compatibilidade legada: duracao_minutos (Math.max(1, Math.round(duracao_segundos / 60)))

export interface DurationInput {
  duracao_segundos?: number | string | null;
  duracao_minutos?: number | string | null;
  start_time?: string | Date | null;
  end_time?: string | Date | null;
  agendado_para?: string | Date | null;
  data_hora?: string | Date | null;
  data_hora_inicio?: string | Date | null;
  [key: string]: any;
}

export interface StandardDurationOutput {
  duracao_segundos: number;
  duracao_minutos: number;
}

export class DurationConflictError extends Error {
  code: string;
  constructor(message: string, code = 'CONFLITO_PARAMETROS_DURACAO') {
    super(message);
    this.name = 'DurationConflictError';
    this.code = code;
  }
}

export class InvalidDurationError extends Error {
  code: string;
  constructor(message: string, code = 'DURACAO_INVALIDA') {
    super(message);
    this.name = 'InvalidDurationError';
    this.code = code;
  }
}

// Limite técnico máximo: 30 dias (2.592.000 segundos) para evitar overflow ou estouro de memória/datas
export const MAX_DURATION_SECONDS = 30 * 24 * 60 * 60; // 2.592.000s
export const MIN_DURATION_SECONDS = 1; // 1s

/**
 * Valida e normaliza a duração de um agendamento seguindo estritamente a hierarquia:
 * 1. Segundos explícitos válidos (> 0, inteiro).
 * 2. Início (start_time ou agendado_para) e término (end_time) explícitos -> diferença exata em segundos.
 * 3. Minutos legados explícitos (> 0) -> convertidos para segundos exatos (min * 60).
 * 4. Fallback default (em segundos).
 * 
 * Regras estritas:
 * - Se campos explícitos forem conflitantes (ex: duracao_segundos e duracao_minutos divergentes,
 *   ou intervalo start/end divergente de duracao_segundos), REJEITA com DurationConflictError.
 * - Duração zero, negativa, decimal não inteira em segundos ou acima do teto técnico é rejeitada com InvalidDurationError.
 */
export function validateAndNormalizeDuration(
  input: DurationInput,
  defaultSeconds = 3600
): StandardDurationOutput {
  if (!input) {
    const sec = Math.max(MIN_DURATION_SECONDS, Math.round(defaultSeconds));
    return {
      duracao_segundos: sec,
      duracao_minutos: Math.max(1, Math.round(sec / 60))
    };
  }

  const hasSec = input.duracao_segundos !== undefined && input.duracao_segundos !== null && input.duracao_segundos !== '';
  const hasMin = input.duracao_minutos !== undefined && input.duracao_minutos !== null && input.duracao_minutos !== '';

  const startStr = input.start_time || input.agendado_para || input.data_hora || input.data_hora_inicio;
  const endStr = input.end_time;
  const hasInterval = Boolean(startStr && endStr);

  let parsedSecFromInterval: number | null = null;
  if (hasInterval) {
    const sDate = new Date(startStr!);
    const eDate = new Date(endStr!);
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
      throw new InvalidDurationError('Data/hora de início ou término inválida para cálculo de duração.');
    }
    const diffMs = eDate.getTime() - sDate.getTime();
    if (diffMs <= 0) {
      throw new InvalidDurationError('Data/hora de término (end_time) deve ser estritamente posterior ao início (start_time).');
    }
    parsedSecFromInterval = Math.round(diffMs / 1000);
    if (parsedSecFromInterval > MAX_DURATION_SECONDS) {
      throw new InvalidDurationError(`Intervalo excede o limite técnico máximo permitido de 30 dias (${MAX_DURATION_SECONDS} segundos).`);
    }
  }

  // 1. Validar segundos explícitos se fornecidos
  let parsedExplicitSec: number | null = null;
  if (hasSec) {
    const rawVal = Number(input.duracao_segundos);
    if (isNaN(rawVal) || !Number.isFinite(rawVal) || !Number.isInteger(rawVal) || rawVal <= 0) {
      throw new InvalidDurationError(
        `Duração em segundos inválida (${input.duracao_segundos}). Deve ser um número inteiro estritamente positivo (> 0).`
      );
    }
    if (rawVal > MAX_DURATION_SECONDS) {
      throw new InvalidDurationError(
        `Duração em segundos (${rawVal}s) excede o limite técnico máximo de 30 dias (${MAX_DURATION_SECONDS}s).`
      );
    }
    parsedExplicitSec = rawVal;
  }

  // 2. Validar minutos explícitos se fornecidos
  let parsedExplicitMinToSec: number | null = null;
  if (hasMin) {
    const rawMin = Number(input.duracao_minutos);
    if (isNaN(rawMin) || !Number.isFinite(rawMin) || rawMin <= 0) {
      throw new InvalidDurationError(
        `Duração em minutos inválida (${input.duracao_minutos}). Deve ser um número maior que zero.`
      );
    }
    parsedExplicitMinToSec = Math.round(rawMin * 60);
    if (parsedExplicitMinToSec > MAX_DURATION_SECONDS) {
      throw new InvalidDurationError(
        `Duração em minutos (${rawMin}m) excede o limite técnico máximo de 30 dias.`
      );
    }
  }

  // 3. Checar conflito entre duracao_segundos e duracao_minutos explícitos
  if (parsedExplicitSec !== null && parsedExplicitMinToSec !== null) {
    const expectedMinFromSec = Math.max(1, Math.round(parsedExplicitSec / 60));
    const providedMin = Number(input.duracao_minutos);
    
    // Conflito: minutos explicitamente passados divergem tanto em segundos exatos quanto na projeção esperada
    if (parsedExplicitSec !== parsedExplicitMinToSec && Math.abs(parsedExplicitSec - parsedExplicitMinToSec) > 30 && providedMin !== expectedMinFromSec) {
      throw new DurationConflictError(
        `Parâmetros conflitantes: 'duracao_segundos' (${parsedExplicitSec}s) e 'duracao_minutos' (${input.duracao_minutos}m = ${parsedExplicitMinToSec}s) possuem valores divergentes.`
      );
    }
  }

  // 4. Checar conflito com o intervalo start_time / end_time se ambos foram passados
  if (parsedSecFromInterval !== null) {
    if (parsedExplicitSec !== null && Math.abs(parsedSecFromInterval - parsedExplicitSec) > 1) {
      throw new DurationConflictError(
        `Parâmetros conflitantes: Intervalo entre start e end (${parsedSecFromInterval}s) diverge de 'duracao_segundos' (${parsedExplicitSec}s).`
      );
    }
    if (parsedExplicitSec === null && parsedExplicitMinToSec !== null && Math.abs(parsedSecFromInterval - parsedExplicitMinToSec) > 30) {
      throw new DurationConflictError(
        `Parâmetros conflitantes: Intervalo entre start e end (${parsedSecFromInterval}s) diverge de 'duracao_minutos' (${input.duracao_minutos}m = ${parsedExplicitMinToSec}s).`
      );
    }
  }

  // Determinar duração canônica final em segundos
  let finalSeconds: number;
  if (parsedExplicitSec !== null) {
    finalSeconds = parsedExplicitSec;
  } else if (parsedSecFromInterval !== null) {
    finalSeconds = parsedSecFromInterval;
  } else if (parsedExplicitMinToSec !== null) {
    finalSeconds = parsedExplicitMinToSec;
  } else {
    finalSeconds = Math.max(MIN_DURATION_SECONDS, Math.round(defaultSeconds));
  }

  if (finalSeconds <= 0 || !Number.isInteger(finalSeconds)) {
    throw new InvalidDurationError('Duração final calculada deve ser um número inteiro de segundos maior que zero.');
  }

  // Projeção legada em minutos: mínimo 1 min para evitar violar constraints legadas (duracao_minutos > 0)
  const finalMinutes = Math.max(1, Math.round(finalSeconds / 60));

  return {
    duracao_segundos: finalSeconds,
    duracao_minutos: finalMinutes
  };
}

/**
 * Normaliza a duração retornando apenas os segundos.
 */
export function normalizeDuration(input: DurationInput, defaultMinutos = 60): number {
  try {
    const result = validateAndNormalizeDuration(input, defaultMinutos * 60);
    return result.duracao_segundos;
  } catch (err) {
    if (err instanceof DurationConflictError || err instanceof InvalidDurationError) {
      throw err;
    }
    return Math.max(1, Math.round(defaultMinutos * 60));
  }
}

/**
 * Leitura canônica e infalível da duração de um agendamento em segundos.
 * Regra: duracao_segundos (se positivo) > duracao_minutos * 60 (se positivo) > fallback.
 * NUNCA reconstrói segundos a partir de minutos se duracao_segundos já existir!
 */
export function getAppointmentDurationSeconds(appt: any, fallbackSeconds = 3600): number {
  if (!appt) return fallbackSeconds;
  
  if (typeof appt.duracao_segundos === 'number' && appt.duracao_segundos > 0) {
    return Math.round(appt.duracao_segundos);
  }
  
  if (typeof appt.duracao_segundos === 'string' && !isNaN(Number(appt.duracao_segundos)) && Number(appt.duracao_segundos) > 0) {
    return Math.round(Number(appt.duracao_segundos));
  }

  if (typeof appt.duracao_minutos === 'number' && appt.duracao_minutos > 0) {
    return Math.round(appt.duracao_minutos * 60);
  }

  if (typeof appt.duracao_minutos === 'string' && !isNaN(Number(appt.duracao_minutos)) && Number(appt.duracao_minutos) > 0) {
    return Math.round(Number(appt.duracao_minutos) * 60);
  }

  // Tentar calcular via start/end se disponíveis
  const start = appt.agendado_para || appt.start_time || appt.start?.dateTime;
  const end = appt.end_time || appt.end?.dateTime;
  if (start && end) {
    const diff = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
    if (!isNaN(diff) && diff > 0) {
      return Math.round(diff);
    }
  }

  return fallbackSeconds;
}

/**
 * Retorna a duração em minutos apenas para fins de exibição legada.
 */
export function getAppointmentDurationMinutes(appt: any, fallbackMinutes = 60): number {
  const seconds = getAppointmentDurationSeconds(appt, fallbackMinutes * 60);
  return Math.max(1, Math.round(seconds / 60));
}

/**
 * Calcula a data/hora exata de término do agendamento somando os segundos canônicos.
 */
export function calculateAppointmentEndTime(start: Date | string, durationSeconds: number): Date {
  const s = new Date(start);
  return new Date(s.getTime() + durationSeconds * 1000);
}

/**
 * Verifica se dois agendamentos se sobrepõem, considerando o buffer após cada evento.
 * Intervalo ocupado pelo evento A: [startA, startA + durSecA + bufferSecA)
 * Intervalo ocupado pelo evento B: [startB, startB + durSecB + bufferSecB)
 * 
 * Regra de sobreposição estrita em semi-intervalos [S, E):
 * StartA < EndB AND EndA > StartB
 */
export function isOverlappingWithBuffer(
  startA: Date | string,
  durSecA: number,
  bufferSecA: number = 0,
  startB: Date | string,
  durSecB: number,
  bufferSecB: number = 0
): boolean {
  const sA = new Date(startA).getTime();
  const eA = sA + (durSecA + Math.max(0, bufferSecA)) * 1000;
  const sB = new Date(startB).getTime();
  const eB = sB + (durSecB + Math.max(0, bufferSecB)) * 1000;

  return sA < eB && eA > sB;
}

/**
 * Compatibilidade legada para isOverlapping simples sem buffer.
 */
export function isOverlapping(
  startA: Date | string,
  durationSecA: number,
  startB: Date | string,
  durationSecB: number
): boolean {
  return isOverlappingWithBuffer(startA, durationSecA, 0, startB, durationSecB, 0);
}

/**
 * Adiciona duracao_segundos e duracao_minutos canônicos a um payload de gravação.
 */
export function writeDuration<T extends Record<string, any>>(
  payload: T,
  defaultSeconds = 3600
): T & StandardDurationOutput {
  const { duracao_segundos, duracao_minutos } = validateAndNormalizeDuration(payload, defaultSeconds);
  return {
    ...payload,
    duracao_segundos,
    duracao_minutos
  };
}

/**
 * Formata segundos em texto humanizado compacto e preciso.
 * Exemplos:
 * 61s -> "1m 1s"
 * 2323s -> "38m 43s"
 * 3600s -> "1h"
 * 5400s -> "1h 30m"
 * 30s -> "30s"
 */
export function formatDurationLabel(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  const parts: string[] = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Converte Horas, Minutos e Segundos para total em segundos.
 */
export function parseDurationFromHms(hours = 0, minutes = 0, seconds = 0): number {
  const h = Math.max(0, Math.floor(Number(hours) || 0));
  const m = Math.max(0, Math.floor(Number(minutes) || 0));
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return h * 3600 + m * 60 + s;
}

/**
 * Decompõe total de segundos em Horas, Minutos e Segundos.
 */
export function splitDurationToHms(totalSeconds: number): { hours: number; minutes: number; seconds: number } {
  const sec = Math.max(0, Math.round(totalSeconds || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  return { hours, minutes, seconds };
}

/**
 * Faz parsing de string de duração em formatos como:
 * - "38m43s" / "38m 43s" -> 2323
 * - "1h30m" / "1h 30min" -> 5400
 * - "61s" / "61 seg" -> 61
 * - "45m" / "45 min" -> 2700
 * - "2h" -> 7200
 */
export function parseDurationStringToSeconds(str: string): number | null {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim().toLowerCase();
  if (!trimmed) return null;

  // Formato numérico puro (ex: "3600" ou "61")
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  let totalSec = 0;
  let matched = false;

  const hMatch = trimmed.match(/(\d+)\s*(?:h|hora|horas)/);
  if (hMatch) {
    totalSec += parseInt(hMatch[1], 10) * 3600;
    matched = true;
  }

  const mMatch = trimmed.match(/(\d+)\s*(?:m|min|minuto|minutos)/);
  if (mMatch) {
    totalSec += parseInt(mMatch[1], 10) * 60;
    matched = true;
  }

  const sMatch = trimmed.match(/(\d+)\s*(?:s|seg|segundo|segundos)/);
  if (sMatch) {
    totalSec += parseInt(sMatch[1], 10);
    matched = true;
  }

  return matched && totalSec > 0 ? totalSec : null;
}

/**
 * Formata um instante ISO ou Date respeitando o fuso horário configurado da loja.
 * Não altera o instante UTC real.
 */
export function formatTimeInTimezone(
  date: Date | string,
  timeZone = 'America/Sao_Paulo',
  locale = 'pt-BR'
): string {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(locale, {
      timeZone: timeZone || 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return '';
  }
}
